import { Component, ViewChild } from '@angular/core';
import { NgClass } from '@angular/common';

import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { NoopScrollStrategy, Overlay } from '@angular/cdk/overlay';

import { lastValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AppConfig } from '@project_app/config/app.config';
import { environment } from '@environment';

import { BaseComponent } from '@core/components/base/base.component';
import { AnnouncementComponent } from '@core/components/announcement/announcement.component';
import { BrandNewsComponent } from '@project_app/components/brand-news/brand-news.component';
import { PluginVideoComponent } from '@shared/components/plugin-video/plugin-video.component';
import { FeedbackDialogComponent } from '@shared/components/feedback/feedback-dialog/feedback-dialog.component';
import { BottomStripCarouselComponent } from '@shared/components/bottom-strip-carousel/bottom-strip-carousel.component';
import { BottomNavigationComponent } from '@shared/components/bottom-navigation/bottom-navigation.component';
import { BirthdayAnimationComponent } from '@shared/components/birthday-animation/birthday-animation.component';
import { BrandsLoaderComponent } from './components/brands-loader/brands-loader.component';
import { OutletStatusStripComponent } from '@app/shared/components/outlet-status-strip/outlet-status-strip.component';
import { HomePageBannersComponent } from './components/home-page-banners/home-page-banners.component';
import { ShopByCategoryComponent } from './components/shop-by-category/shop-by-category.component';
import { ShopByMembershipComponent } from './components/shop-by-membership/shop-by-membership.component';

import { OutletService } from '@core/services/outlet/outlet.service';
import { OutletStatusService } from '@core/services/outlet/outlet-status.service';
import { StorageService } from '@core/services/util/storage.service';
import { LoginService } from '@core/services/login/login.service';
import { ToastrService } from '@core/services/notification/toastr.service';
import { CustomerApiService } from '@core/http/customer-api.service';
import { ImageUrlCreatorService } from '@core/services/imageUrlCreator/image-url-creator.service';
import { MembershipService } from '@core/services/membership/membership.service';
import { BannerService } from '@core/services/banner/banner.service';
import { GtmService } from '@core/services/analytics/gtm.service';
import { OnboardingService } from '@core/services/onboarding/onboarding.service';
import { PassService } from '@app/core/services/pass/pass.service';
import { PslaService } from '@app/core/services/psla/psla.service';
import { CartService } from '@app/core/services/cart/cart.service';
import { OrderApiService } from '@app/core/http/order-api.service';
import { CustomerService } from '@app/core/services/customer/customer.service';
import { ThirdPartyScriptService } from '@app/core/services/third-party-script/third-party-script.service';
import { SessionStorageService } from '@app/core/services/util/session-storage.service';
import { OutletStatusStripService } from '@app/core/services/outlet-status-strip/outlet-status-strip.service';

import { cartStrip, BottomStripDetail } from '@app/shared/models/bottom-strip';
import { ActionType } from '@app/shared/enums/bottom-strip';
import { OrderDetailsApiResponse } from '@app/shared/models/api-response/order-details-response';
import { OutletState, CatalogApiResponse, Membership, OutletStatus } from '@app/shared/models/api-response/catalog-api-response';

@Component({
    selector: 'app-brands',
    templateUrl: './brands.component.html',
    styleUrls: ['./brands.component.scss'],
    standalone: true,
    imports: [
        BrandsLoaderComponent,
        NgClass,
        BrandNewsComponent,
        BirthdayAnimationComponent,
        BottomNavigationComponent,
        BottomStripCarouselComponent,
        OutletStatusStripComponent,
        HomePageBannersComponent,
        ShopByCategoryComponent,
        ShopByMembershipComponent
    ]
})
export class BrandsComponent extends BaseComponent {
    brandId: number;
    lottieUrl: string;

    isBrandRating = false;
    fetchedAdditionalApi = false;
    isThreeTileLayout = true;
    showLoading = true;
    isEatClubApp = false;
    showMenuFirst: boolean;

    outlet: CatalogApiResponse['outlet'];
    landingPageBanners = {};

    banners = [];
    outletBanners = {};
    memberships: Membership[] = [];

    showBottomStrips = true;
    bottomStrips: BottomStripDetail[] = [];

    outletState: string;
    runningOrderTrackingId: string;
    showOutletStatusStrip = false;
    outletStatusStrip: OutletStatus | null;

    destroySubscription$: Subject<void> = new Subject();
    pluginVideoDialogRef: MatDialogRef<PluginVideoComponent>;

    @ViewChild(BottomStripCarouselComponent) bottomStripCarouselComponent: BottomStripCarouselComponent;

    constructor(
        private overlay: Overlay,
        private dialog: MatDialog,
        private storageService: StorageService,
        private loginService: LoginService,
        private customerApiService: CustomerApiService,
        private outletService: OutletService,
        private outletStatusService: OutletStatusService,
        private outletStatusStripService: OutletStatusStripService,
        private membershipService: MembershipService,
        private toastr: ToastrService,
        private cartService: CartService,
        private bannerService: BannerService,
        private imageUrlCreator: ImageUrlCreatorService,
        private gtmService: GtmService,
        private onboardingService: OnboardingService,
        private passService: PassService,
        private pslaService: PslaService,
        private orderApiService: OrderApiService,
        private customerService: CustomerService,
        private thirdPartyScript: ThirdPartyScriptService,
        private sessionStorageService: SessionStorageService
    ) {
        super();
        this.brandId = environment.brand.id;
        this.isEatClubApp = this.brandId === 19;
        this.initialiseLottieUrl();
    }

    initialiseLottieUrl() {
        if (this.brandId === 1) {
            const lottieFile = this.isMobile ? 'mobile-moving-banner' : 'desktop-moving-banner';
            this.lottieUrl = `${environment.assetsUrl}/lottie_files/${environment.brand.id}/${lottieFile}`;
        }
    }

    ngOnInitBrowser() {
        // reset brand
        this.storageService.delete('brand_id');
        this.outletService.getOutletData(this.brandId);

        this.outletService.state.pipe(takeUntil(this.destroySubscription$)).subscribe((data: CatalogApiResponse) => {
            if (!data || !Object.keys(data).length) {
                return;
            }

            this.showLoading = true;

            this.gtmService.logPageDetails(data, 'home_page');
            this.outletStatusService.checkOutletStatus(data);

            this.initialiseOutlet(data);
            this.processMembershipsAndBanners(data);
            this.fetchAdditionalDataIfNeeded(data);
            this.handleLandingPageBanners(data);

            this.handleMultipleTypesOfStrips();
            this.passService.initCustomerPasses();
            this.showLoading = false;
        });

        this.cartService.state.pipe(takeUntil(this.destroySubscription$)).subscribe(() => {
            this.handleMultipleTypesOfStrips(); //for supersavers
        });

        this.showMenuFirst = this._showMenuFirst();
        this.loginService.userLoginSuccess.pipe(takeUntil(this.destroySubscription$)).subscribe(() => {
            this.fetchedAdditionalApi = false;
            this.fetchadditionalApi(true);
            this.showMenuFirst = this._showMenuFirst();
        });

        this.loginService.userLogoutSuccess.pipe(takeUntil(this.destroySubscription$)).subscribe(() => {
            this.bottomStrips = [];
            this.runningOrderTrackingId = '';
            this.handleMultipleTypesOfStrips();
        });

        this.isBrandRating = AppConfig.isBrandRating;
    }

    initialiseOutlet(data: CatalogApiResponse): void {
        const categoryLayout = this.membershipService.getCategoryLayout(data.outlet);
        this.outlet = data.outlet;
        this._handleOnboarding(data);
        this._handleOutletStatusStrip(data);

        if (categoryLayout) {
            this.isThreeTileLayout = categoryLayout === '3-tile-layout';
        }
    }

    processMembershipsAndBanners(data: CatalogApiResponse): void {
        if (!data.outlet.memberships || !data.outlet.memberships.length) return;

        this.memberships = data.outlet.memberships.sort((a, b) => a.seq - b.seq);
        this.memberships = this.memberships.map(member => {
            member.category = member.category || [];
            if (member.id === data.outlet.brand_id) {
                member.category = data.outlet.product_categories;
                member.category.forEach(category => {
                    category.image = this.imageUrlCreator.getCategoryImage(category, 'landing', this.isThreeTileLayout);
                });
            }
            return member;
        });

        this.outletBanners = {
            ...data.outlet.banners,
            main_banners: this.bannerService.getBannerImages(data.outlet.banners['main_banners'])
        };
    }

    fetchAdditionalDataIfNeeded(data: CatalogApiResponse): void {
        const isLoggedIn = this._isLoggedIn();
        this.fetchadditionalApi(isLoggedIn);

        const promotionalBannerPresent = this.outletBanners['main_promotional_card']?.length;
        this.pslaService.getPSLAdata(promotionalBannerPresent, data.outlet);
    }

    handleLandingPageBanners(data: CatalogApiResponse): void {
        const bannersToBeFetched = ['mweb_offer', 'pip'];

        this.bannerService.fetchBannersFromArray(bannersToBeFetched, environment.brand.id).then((banner: any) => {
            this.landingPageBanners = banner;

            if (!this.outletStatusStrip) {
                const announcementImageUrl = this.bannerService.getAnnouncementUrl(
                    data.outlet.announcement,
                    data.outlet.brand_id
                );
                this._showAnnouncement(announcementImageUrl);
            } else {
                this.pluginVideoDialogRef?.close();
            }
        });
    }

    _handleOnboarding(data: CatalogApiResponse) {
        const outletSelectionShown = this.storageService.get('outlet_selection_shown');
        if (!outletSelectionShown || outletSelectionShown != 'true') {
            this.onboardingService.navigateToOnboarding(data);
        }
    }

    _handleOutletStatusStrip(data: CatalogApiResponse) {
        const { outlet } = data;
        const { outlet_status_strip, outlet_state } = outlet;
        this.outletState = outlet_state;
        this.outletStatusStrip = outlet_status_strip;
        this.outletStatusStripService.hydrateOutletStatusStrip({ ...outlet_status_strip, outlet_state });
    }

    fetchadditionalApi(isLoggedIn: boolean) {
        if (this.fetchedAdditionalApi) {
            return;
        }

        if (isLoggedIn) {
            this._getBottomStrips();
        }
        this.fetchedAdditionalApi = true;
    }

    ngOnDestroy() {
        this.destroySubscription$.next();
        this.destroySubscription$.complete();
        this.dialog.closeAll();
    }

    showFeedback(tracking_id: string, source = 'strip-click') {
        lastValueFrom(this.orderApiService.getLiveOrders(tracking_id))
            .then((res: OrderDetailsApiResponse) => {
                if (res.meta.code === 200) {
                    this.openFeedbackDialog(res.order);
                } else if (source === 'strip-click') {
                    this.toastr.openSnackBar(res.meta.message, 'OKAY');
                }
            })
            .catch(err => {
                if (source === 'strip-click') {
                    this.toastr.openSnackBar(err?.meta?.message, 'OKAY');
                }
            });
    }

    openFeedbackDialog(order) {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.panelClass = 'feedback-dialog';
        dialogConfig.data = { order };
        dialogConfig.scrollStrategy = new NoopScrollStrategy();

        dialogConfig.autoFocus = false;

        const dialogRef = this.dialog.open(FeedbackDialogComponent, dialogConfig);

        dialogRef.afterClosed().subscribe(data => {
            if (data) {
                this.closeFeedback(data);
            }
        });
    }

    closeFeedback(data) {
        if (data.saved) {
            // bottomStripCarouselComponent won't be present incase outlet status strip is present,
            //  so for this case we don't have to remove feedback strip as well.
            if (this.bottomStripCarouselComponent) {
                this.bottomStripCarouselComponent.stripRemoved(ActionType.Feedback);
            }

            this._getBottomStrips();
        }
    }

    _isLoggedIn() {
        return Boolean(this.storageService.get('session-id') && this.storageService.get('phone_no'));
    }

    _showMenuFirst() {
        const isOldCustomer = Boolean(this.storageService.get('referral_code'));
        return this._isLoggedIn() && isOldCustomer;
    }

    _getBottomStrips() {
        lastValueFrom(this.customerApiService.getBottomStrips())
            .then(data => {
                if (data.meta.code !== 200) {
                    return;
                }

                const { bottom_strip_details } = data;

                this.checkForFeedback(bottom_strip_details);
                this.customerService.updateCustomerDetails(data);

                const cartHasItem = Boolean(Object.values(this.cartService.state.value.items).length);

                // add cart strip at first, in case of mobile
                if (cartHasItem && this.isMobile) {
                    this.bottomStrips = [cartStrip];
                } else {
                    this.bottomStrips = [];
                }

                this.bottomStrips = [...this.bottomStrips, ...bottom_strip_details];
                this.runningOrderTrackingId = data.latest_running_order;
                this.handleMultipleTypesOfStrips();
            })
            .catch((e: any) => {
                if (e.error && e.error.meta) {
                    if (e.error.meta.code === 403) {
                        this.loginService.clearSessions();
                    }
                    this.toastr.openSnackBar(e.error.meta.message, 'OKAY');
                }
            });
    }

    handleMultipleTypesOfStrips() {
        // no conflicts return
        if (!this.outletStatusStrip) {
            return;
        }

        this.showBottomStrips = false;
        this.showOutletStatusStrip = false;
        const cartHasItem = Boolean(Object.values(this.cartService.state.value.items).length);

        if (this.runningOrderTrackingId) {
            this.showBottomStrips = true;
            return;
        }

        if (this.outletState === OutletState.PREORDER && cartHasItem) {
            this.showBottomStrips = true;
            this.showOutletStatusStrip = false;
        } else {
            this.showBottomStrips = false;
            this.showOutletStatusStrip = true;
        }
    }

    checkForFeedback(bottomStrips: BottomStripDetail[]) {
        const feedbackStrip = bottomStrips.find(strip => strip.button.action_type === ActionType.Feedback);
        const lastShownFeedbackTrackingId = this.sessionStorageService.get('feedback-modal-tracking-id');
        const latestFeedbackTrackingId = feedbackStrip?.button.params.tracking_id;
        if (feedbackStrip && (!lastShownFeedbackTrackingId || lastShownFeedbackTrackingId !== latestFeedbackTrackingId)) {
            this.showFeedback(latestFeedbackTrackingId, 'onload-feedback-check');
            this.sessionStorageService.set('feedback-modal-tracking-id', latestFeedbackTrackingId);
        }
    }

    openVideoPlatingDialog() {
        const pipBanners = this.landingPageBanners['pip'];
        const videoSrc = pipBanners?.length ? pipBanners[0].banner_url : '';
        const showVideoPlating = this.bannerService.showPlugInVideo(videoSrc);
        if (showVideoPlating) {
            const data = { videoSrc };
            // TODO: Check for safari and add required checks, also multiple subscriber is getting initialised
            this.thirdPartyScript.hlsSDKLoaded.subscribe(() => {
                this.pluginVideoDialogRef = this.dialog.open(PluginVideoComponent, {
                    data,
                    hasBackdrop: false,
                    panelClass: 'plugin-video-dialog',
                    scrollStrategy: this.overlay.scrollStrategies.reposition()
                });
            });
        } else {
            this.pluginVideoDialogRef && this.pluginVideoDialogRef.close();
        }
    }

    private _showAnnouncement(imageUrl) {
        if (!imageUrl) {
            this.openVideoPlatingDialog();
            return;
        }

        const dialogConfig = new MatDialogConfig();
        dialogConfig.data = imageUrl;
        dialogConfig.closeOnNavigation = true;
        dialogConfig.panelClass = 'announcement-dialog';
        this.dialog
            .open(AnnouncementComponent, dialogConfig)
            .afterClosed()
            .subscribe(() => {
                this.openVideoPlatingDialog();
            });
    }
}
